import classNames from 'classnames';
import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';

import styles from './CanvasFrame.module.css';

type CanvasFrameVariant = 'selection' | 'hover' | 'repeat' | 'dropzone';

interface CanvasFrameProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * If you wish to show e.g. a `SelectedItemToolbar` inside the frame, you
   * may pass it as children.
   */
  children?: ReactNode;
  /**
   * Optional label to show at the top edge of the frame.
   */
  label?: string | null;
  /**
   * Which variant of the selection frame to use.
   */
  variant: CanvasFrameVariant;
}

const styleClassesByVariant: Record<CanvasFrameVariant, string> = {
  selection: styles.selection,
  hover: styles.hover,
  repeat: styles.repeat,
  dropzone: styles.dropzone,
};
/**
 * Canvas frame is used for visualizing the selection, hover, repeat, and dropzone areas
 * on the view canvas. It also supports showing a label at the top edge of the frame,
 * which should primarily be used only with the dropzone variant.
 *
 * Note that with the selection variant you'd also probably want to use the `SelectedItemToolbar`
 * component.
 *
 * ### Positioning and sizing
 *
 * Note that unlike with other components, you *need* to provide positional and size styling
 * for this component, either with `style` or `className` prop. Without any styling props
 * the component will show up incorrectly. Alternatively, you may use `ref` for positioning.
 *
 * When using the component, you are responsible for styling:
 * - the position of the frame (e.g. `position`, `left`, `top`, and/or `transform`)
 * - the size of the frame (e.g. `width`, `height`, `right`, and/or `bottom`)
 */
export const CanvasFrame = forwardRef<HTMLDivElement, CanvasFrameProps>((props, ref) => {
  const { className, style, variant = 'selection', label, children } = props;

  return (
    <div
      className={classNames(styleClassesByVariant[variant], className)}
      ref={ref}
      style={style}
    >
      {label && (
        <div className={styles['label-outer']}>
          <div className={styles['label-inner']}>{label}</div>
        </div>
      )}
      {children}
    </div>
  );
});
